import { FormikContextType, useFormikContext } from "formik";
import { TypeCreationParametersTable } from "../TypeCreationParametersTable";
import { ComponentTypeDataForm } from "./ComponentTypeDataForm";
import { TypeStructure } from "./TypeStructure";
import { application } from "../../../../_runtime/bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useLoading } from "../../../../components-common/_shared/loading/use-loading.hook";
import { FC, useEffect, useState } from "react";
import { FormProps } from "@components-common/forms";
import { Loader } from "taltech-styleguide";
import { NestedTabs } from "../../../../components-common/tabs/NestedTabs";
import {
  getElementCategory,
  handleTypeParametersRequests,
  mapPayloadToBackend,
} from "./_helpers";
import { useUserContext } from "../../../../_runtime/iam/session.context";
import { roleMapper } from "../../../../_runtime/iam/session.provider";

const tabs: any = [
  {
    id: "data",
    title: "Data",
    components: [
      {
        Component: ComponentTypeDataForm,
      },
    ],
  },
  {
    id: "structure",
    title: "Structure",
    components: [
      {
        Component: TypeStructure,
      },
    ],
  },
  {
    id: "type_parameters",
    title: "Type Parameters",
    components: [
      {
        Component: TypeCreationParametersTable,
      },
    ],
  },
];

/**
 * Function: mapParametersToCreateType
 *
 * This asynchronous function fetches parameters and materials for a specific component type based on the category derived from the pathname.
 * It updates the formik context with the new parameters and sets the properties of the type parameters tab and material options.
 *
 * @param {FormikContextType<any>} formik - The formik context for managing form state.
 * @param {Function} setLoading - Function to set the loading state.
 * @param {Function} setOriginalParameters - Function to set the original parameters state.
 * @param {string} pathname - The current pathname to determine the category.
 *
 * @returns {Promise<void>} A promise that resolves once the parameters and materials have been fetched and the state has been updated.
 */
const mapParametersToCreateType = async (
  formik: FormikContextType<any>,
  setLoading: any,
  setOverridenParameters: any,
  pathname: string,
  isEditForm: boolean
) => {
  const category = getElementCategory(pathname);
  const componentParameters = await application
    .getHttpClient()
    .get(`/buildingms/categories/parameters?category=${category}`)
    .then((response: any) => {
      return response.body!.items;
    });
  let existentTypeParameters: any[] = [];
  if (isEditForm) {
    existentTypeParameters = await application
      .getHttpClient()
      .get(`/buildingms/types/parameters?elementId=${formik.values.id}`)
      .then((response: any) => {
        setOverridenParameters(response.body!.items);
        return response.body!.items;
      });
  }

  const materials = await application
    .getHttpClient()
    .get(`/buildingsms/materials`)
    .then((response: any) => {
      setLoading(false);
      return response.body!.items;
    });

    if(materials.length === 0) return;

  const newComponentParameters = componentParameters.map((param: any) => {
    const typeParam = existentTypeParameters.find(
      (typeParam: any) => typeParam.parameterId === param.id
    );
    let newParam: any = {
      id: param.id,
      name: param.name,
      description: param.description,
      defaultValue: param.value,
      category: param.category,
      type: param.type,
    };

    if (typeParam) {
      newParam = {
        ...newParam,
        value: typeParam.value,
      };
    }

    return newParam;
  });

  tabs[1].components[0].props = {
    materialOptions: materials,
  };

  if (!isEditForm) {
    formik.setFieldValue("layers.core", [
      {
        location: "CORE",
        name: "",
        materialId: materials[0].id,
        elementId: "",
        thickness: category === "WALLS" ? 0.15 : 1,
        function: "STRUCTURE",
      },
    ]);
  }
  formik.setFieldValue("typeParameters", newComponentParameters);
  tabs[2].components[0].props = {
    tableItems: newComponentParameters,
    title: `${category} Parameters`,
  };
};

/**
 * Functional Component: ComponentTypeScreenTabs
 *
 * This component manages the tabs for the component type screen. It initializes the form state, fetches parameters and materials,
 * and handles form submission for creating or editing component types.
 *
 * Props:
 * @param {FormProps<any>} props - The properties passed to the form component.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the tabs for the component type screen.
 */
export const ComponentTypeScreenTabs: FC<FormProps<any>> = (props) => {
  const nav = useNavigate();
  const isEditForm = props.id === "form_types_edit";
  const [overridenParameters, setOverridenParameters] = useState<any>([]);
  const formik = useFormikContext();
  const [loading, setLoading] = useLoading(true);
  const { pathname } = useLocation();
  const { userId, userRole } = useUserContext();

  useEffect(() => {
    if (isEditForm) {
      const { layers }: any = formik.values;
      const newLayers = {
        interior: layers.filter((layer: any) => layer.location === "INTERIOR"),
        exterior: layers.filter((layer: any) => layer.location === "EXTERIOR"),
        core: layers.filter((layer: any) => layer.location === "CORE"),
      };
      formik.setFieldValue("layers", newLayers);
    }
    mapParametersToCreateType(
      formik,
      setLoading,
      setOverridenParameters,
      pathname,
      isEditForm
    );
  }, []);

  useEffect(() => {
    if (formik.isSubmitting) {
      if (formik.isValid) {
        let { values, typeParameters }: any = mapPayloadToBackend(
          isEditForm,
          formik.values,
          pathname.includes("walls"),
          overridenParameters
        );

        if (!isEditForm) {
          values = {
            ...values,
            sourceId: userId,
            sourceType: userRole === roleMapper.Student ? "USER" : "SYSTEM",
          };
          application
            .getHttpClient()
            .post(
              `/buildingsms/types/elements${
                pathname.includes("walls") ? "/walls" : ""
              }`,
              values
            )
            .then(() => {
              handleTypeParametersRequests(typeParameters);
              nav(-1);
            })
            .catch((error: any) => formik.setSubmitting(false));
        } else {
          application
            .getHttpClient()
            .put(
              `/buildingsms/types/elements${
                pathname.includes("walls")
                  ? `/walls/${values.id}`
                  : `/${values.id}`
              }`,
              values
            )
            .then(() => {
              handleTypeParametersRequests(typeParameters);
              nav(-1);
            })
            .catch((error: any) => formik.setSubmitting(false));
        }
      }
      formik.setSubmitting(false);
    }
  }, [formik.isSubmitting]);

  return loading ? (
    <Loader overlay />
  ) : (
    <NestedTabs tabs={tabs} parentPath="" />
  );
};
